import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Link2, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';

const API_URL = window.__BACKEND_URL;

const PLATFORM_INFO = {
  tiktok: {
    name: 'TikTok',
    color: 'from-pink-500 to-cyan-500',
    icon: '🎵'
  },
  instagram: {
    name: 'Instagram',
    color: 'from-purple-500 to-pink-500',
    icon: '📸'
  },
  youtube: {
    name: 'YouTube',
    color: 'from-red-500 to-red-600',
    icon: '📺'
  },
  facebook: {
    name: 'Facebook',
    color: 'from-blue-500 to-blue-600',
    icon: '👍'
  }
};

export default function SocialAccounts() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [postingSafety, setPostingSafety] = useState(null);

  useEffect(() => {
    loadBrands();
    loadPostingSafety();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      loadAccounts();
    }
  }, [selectedBrand]);

  const loadBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/internal-brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data);
      if (response.data.length > 0 && !selectedBrand) {
        setSelectedBrand(response.data[0].id);
      }
    } catch (err) {
      console.error('Error loading brands:', err);
    }
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/social-accounts?brand_id=${selectedBrand}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
      setError(null);
    } catch (err) {
      setError('Error loading accounts: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadPostingSafety = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/posting-safety/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPostingSafety(response.data);
    } catch (err) {
      console.error('Error loading posting safety:', err);
    }
  };

  const connectAccount = async (platform) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/social-accounts/connect`, {
        brand_id: selectedBrand,
        platform: platform
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${response.data.message}`);
      await loadAccounts();
      setError(null);
    } catch (err) {
      setError('Error connecting account: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const testPermissions = async (accountId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/social-accounts/${accountId}/test-permissions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${response.data.message}\n\nTests passed: ${response.data.all_passed ? 'Yes' : 'No'}`);
      await loadAccounts();
      setError(null);
    } catch (err) {
      setError('Error testing permissions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const disconnectAccount = async (accountId) => {
    if (!confirm('Are you sure you want to disconnect this account?')) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/social-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadAccounts();
      setError(null);
    } catch (err) {
      setError('Error disconnecting account: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const setupDropboxFolders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/brands/${selectedBrand}/setup-dropbox-folders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${response.data.message}`);
      setError(null);
    } catch (err) {
      setError('Error setting up folders: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const selectedBrandData = brands.find(b => b.id === selectedBrand);
  const connectedPlatforms = new Set(accounts.map(a => a.platform));
  const availablePlatforms = Object.keys(PLATFORM_INFO).filter(p => !connectedPlatforms.has(p));

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
            Social Accounts
          </h1>
          <p className="text-gray-400">Manage social media connections per brand</p>
        </div>

        {/* Posting Safety Warning */}
        {postingSafety && !postingSafety.posting_enabled && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-300 font-medium">Posting Safety is OFF</p>
              <p className="text-sm text-yellow-400/80 mt-1">
                Connected accounts will not post content until posting is enabled in Settings.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Brand Selector */}
        <div className="mb-8 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Select Brand</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {brands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => setSelectedBrand(brand.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedBrand === brand.id
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
              >
                <h3 className="text-lg font-semibold text-white mb-1">{brand.display_name}</h3>
                <p className="text-sm text-gray-400">{brand.slug}</p>
              </button>
            ))}
          </div>
          
          {selectedBrandData && (
            <div className="mt-4">
              <button
                onClick={setupDropboxFolders}
                disabled={loading}
                className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50 text-sm"
              >
                📁 Setup Dropbox Folders
              </button>
            </div>
          )}
        </div>

        {/* Connected Accounts */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Connected Accounts</h2>
          
          {accounts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accounts.map((account) => {
                const platform = PLATFORM_INFO[account.platform];
                return (
                  <div
                    key={account.id}
                    className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${platform.color} flex items-center justify-center text-2xl`}>
                          {platform.icon}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{platform.name}</h3>
                          <p className="text-sm text-gray-400">{account.platform_username || 'Not set'}</p>
                        </div>
                      </div>
                      {account.is_connected ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                    </div>

                    {/* Permission Tests */}
                    {account.permissions_tested && (
                      <div className="mb-4 p-3 bg-black/20 rounded-lg">
                        <p className="text-xs font-medium text-gray-400 mb-2">Permission Tests</p>
                        <div className="space-y-1">
                          {Object.entries(account.permissions_test_results || {}).map(([key, result]) => (
                            <div key={key} className="flex items-center gap-2 text-xs">
                              {result.passed ? (
                                <CheckCircle className="w-3 h-3 text-green-400" />
                              ) : (
                                <XCircle className="w-3 h-3 text-red-400" />
                              )}
                              <span className={result.passed ? 'text-green-300' : 'text-red-300'}>
                                {result.message}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Capabilities */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {account.can_post_videos && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                          📹 Videos
                        </span>
                      )}
                      {account.can_post_images && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs">
                          🖼️ Images
                        </span>
                      )}
                      {account.can_post_text && (
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">
                          📝 Text
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {!account.permissions_tested && (
                        <button
                          onClick={() => testPermissions(account.id)}
                          disabled={loading}
                          className="flex-1 px-3 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50 text-sm"
                        >
                          Test Permissions
                        </button>
                      )}
                      <button
                        onClick={() => disconnectAccount(account.id)}
                        disabled={loading}
                        className="px-3 py-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50 text-sm"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No social accounts connected yet
            </div>
          )}
        </div>

        {/* Available Platforms */}
        {availablePlatforms.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Connect Platform</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {availablePlatforms.map((platform) => {
                const info = PLATFORM_INFO[platform];
                return (
                  <button
                    key={platform}
                    onClick={() => connectAccount(platform)}
                    disabled={loading || !selectedBrand}
                    className={`p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg hover:border-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className={`w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br ${info.color} flex items-center justify-center text-3xl`}>
                      {info.icon}
                    </div>
                    <p className="text-white font-medium">{info.name}</p>
                    <p className="text-xs text-gray-400 mt-1">Click to connect</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {brands.length === 0 && (
          <div className="text-center py-12">
            <Link2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">No brands found</p>
            <p className="text-sm text-gray-500">Seed internal brands first from Self-Marketing page</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
